import { differenceInYears } from 'date-fns';
import { computed, type ComputedRef } from 'vue';
import { useYieldManagementSettings } from '@/queries/use-yield-management-settings.ts';
import type { Booking } from '@/entities/bookings/booking.entity.ts';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import {
  PriceRecalculationMode,
  ValueType,
  VehicleExtraType,
  CounterpartyType,
} from '@carvia/ros-client-types';
import { fromCalendarDate } from '@/utils/calendar-date';
import { getBookedExtras, getCustomBookedExtras, isLongTermPricing } from '@/utils/pricing.utils';

export const useYoungDrivers = (booking: ComputedRef<Booking>) => {
  const { data: yieldManagementSettings } = useYieldManagementSettings();
  const { mutateAsync: updateBooking } = useUpdateBooking();

  const isYoungDriver = (dateOfBirth: Date) => {
    if (!yieldManagementSettings.value) {
      return false;
    }
    return (
      differenceInYears(new Date(), fromCalendarDate(dateOfBirth)) <
      yieldManagementSettings.value.youngDriverUntilAge
    );
  };

  const numBookedYoungDrivers = computed(() => {
    const bookedCustomExtra = getCustomBookedExtras(booking.value.priceCalculation).find(
      (extra) => extra.customVehicleExtraType === VehicleExtraType.YOUNG_DRIVER,
    );
    if (bookedCustomExtra) {
      return Number(bookedCustomExtra.value);
    }
    const bookedExtra = getBookedExtras(booking.value.priceCalculation).find(
      (extra) => extra.vehicleExtraSnapshot.type === VehicleExtraType.YOUNG_DRIVER,
    );
    if (bookedExtra) {
      if (bookedExtra.vehicleExtraOptionSnapshot.valueType === ValueType.BOOLEAN) {
        return bookedExtra.vehicleExtraOptionSnapshot.value === 'true' ? 1 : 0;
      }
      return Number(bookedExtra.vehicleExtraOptionSnapshot.value);
    }
    return 0;
  });

  const numYoungDrivers = computed(() => {
    if (!yieldManagementSettings.value) {
      return null;
    }
    if (isLongTermPricing(booking.value.priceCalculation)) {
      const dateOfBirth = booking.value.customerData?.dateOfBirth;
      if (!dateOfBirth) {
        return 0;
      }
      return isYoungDriver(fromCalendarDate(dateOfBirth)) ? 1 : 0;
    }
    const datesOfBirth: Date[] = [];
    if (booking.value.customerData?.dateOfBirth) {
      datesOfBirth.push(fromCalendarDate(booking.value.customerData.dateOfBirth));
    }
    booking.value.additionalDrivers.forEach((driver) => {
      if (driver.dateOfBirth) {
        datesOfBirth.push(fromCalendarDate(driver.dateOfBirth));
      }
    });
    return datesOfBirth.filter((dateOfBirth) => isYoungDriver(dateOfBirth)).length;
  });

  const hasMoreYoungDriversThenBooked = computed(() => {
    if (numYoungDrivers.value === null) {
      return null;
    }
    return numBookedYoungDrivers.value < numYoungDrivers.value;
  });

  const updateYoungDriverExtra = async () => {
    if (isLongTermPricing(booking.value.priceCalculation) || numYoungDrivers.value === null) {
      return false;
    }
    const extra = booking.value.priceCalculation.availableExtras?.find(
      (extra) => extra.type === VehicleExtraType.YOUNG_DRIVER,
    );
    const option = extra?.options.find((option) => Number(option.value) === numYoungDrivers.value);
    if (!extra || !option) {
      return false;
    }

    await updateBooking({
      id: booking.value.id,
      booking: {
        pricing: {
          priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
          priceDifferenceCounterparty: CounterpartyType.CUSTOMER,
          bookedExtras: [
            {
              vehicleExtraId: extra.id,
              vehicleExtraOptionId: option.id,
            },
          ],
        },
      },
      query: { handoverAdjustment: true },
    });
    return true;
  };

  return {
    numBookedYoungDrivers,
    numYoungDrivers,
    hasMoreYoungDriversThenBooked,
    updateYoungDriverExtra,
  };
};
